<!--
  SocialElement.vue

  Displays a single social contact as an icon-only link chip — the building
  block for the `socials` field (parallel to ContactElement / SourceElement).
  The icon is the platform's brand mark (from SOCIAL_PLATFORMS, keyed by the
  contact row's `label`); when it resolves to a URL the chip links to the
  profile, titled with the raw value, otherwise it renders as a plain span.

  Props:
    - value: A social contact row ({ label?, value? }).
    - size: Chip size ('small', 'normal').
    - fixed: Render as a plain span (no navigation), matching the element-chip
      convention used for dropdown suggestions.
-->
<template>
  <component
    :is="interactive ? 'a' : 'span'"
    v-bind="interactive ? { href, target: '_blank', rel: 'noopener noreferrer' } : {}"
    class="social-element"
    :class="sizeClass"
    :title="title"
  >
    <font-awesome-icon :icon="icon" class="icon" />
  </component>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { SOCIAL_PLATFORMS } from "@auger/core/fields"

interface Props {
  value: { label?: string | null; value?: string | null }
  size?: string
  fixed?: boolean
  readonly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: "normal",
  fixed: false,
  readonly: true,
})

// The prop is named `value` to match the element-component convention; the
// payload is a social contact row (a `record_contacts` row whose `label` names
// a platform).
const contact = computed(() => props.value)

const sizeClass = computed(() => (props.size === "small" ? "is-small" : null))

const icon = computed(
  () =>
    SOCIAL_PLATFORMS.find((p) => p.key === contact.value?.label?.toLowerCase())?.icon ?? [
      "fas",
      "square-up-right",
    ]
)

const title = computed(() => contact.value?.value ?? "")

const href = computed(() => {
  const v = contact.value?.value ?? ""
  if (!v) return null
  return /^https?:\/\//.test(v) ? v : `https://${v}`
})

// Navigate only when read-only, not `fixed`, and a URL resolves; otherwise it
// reads as a token, matching the other element chips.
const interactive = computed(() => props.readonly && !props.fixed && !!href.value)
</script>

<style>
.social-element {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: var(--auger-link);
  color: var(--auger-text-inverted);

  &.is-small {
    /* width: var(--auger-size-7);
    height: var(--auger-size-7); */
    width: 19px;
    height: 19px;
    border-radius: 4px;
  }
}

a.social-element:hover {
  opacity: 1;
  color: var(--auger-text-inverted);
  background: var(--auger-link-dark);
}
</style>
